import {
  OrderType,
  SearchLearningObjectiveStatementsDocument,
  useDisableLearningObjectiveStatementMutation,
  useGetLearningObjectiveStatementByIdQuery,
} from '@exam-management/graphql';
import { useQBankStructure } from '@exam-management/qbs-provider';
import { ConfirmationModal, Flex, Text } from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { useMemo } from 'react';

interface DisableLOSModalProps {
  losId: string | null;
  open: boolean;
  onClose: () => void;
}

export default function DisableLOSModal(props: Readonly<DisableLOSModalProps>) {
  const { losId, open: isOpen, onClose } = props;

  const { showSnackbar } = useSnackbar();
  const { qBankStructureInformation } = useQBankStructure();
  const { id: qBankStructureId } = qBankStructureInformation;

  const [disableLOS, { loading: isDisablingLOS }] = useDisableLearningObjectiveStatementMutation();

  const searchLOSVariables = useMemo(
    () => ({
      order: { field: 'updatedAt', type: OrderType.Asc },
      paging: { currentPage: 0, pageSize: 9999 },
      search: { qBankStructureId },
    }),
    [qBankStructureId],
  );

  const { data: losData, loading: isLoadingLOS } = useGetLearningObjectiveStatementByIdQuery({
    variables: { input: { id: losId ?? '' } },
    skip: !losId || !isOpen,
  });

  const chapterLevelItemId =
    losData?.qbsGetLearningObjectiveStatementById?.qBankStructureLevelItem?.id ?? '';

  const handleDisable = async () => {
    if (!losId || !chapterLevelItemId) return;

    await disableLOS({
      variables: {
        input: {
          qBankStructureId,
          chapterLevelItemId,
          learningObjectiveStatementId: losId,
        },
      },
      refetchQueries: [
        {
          query: SearchLearningObjectiveStatementsDocument,
          variables: searchLOSVariables,
        },
      ],
      awaitRefetchQueries: true,
      onCompleted: () => {
        onClose();
        showSnackbar({
          message: 'LOS has been disabled',
          variant: 'success',
        });
      },
      onError: () => {
        showSnackbar({
          message: 'Unable to Disable LOS',
          variant: 'error',
        });
      },
    });
  };

  return (
    <ConfirmationModal
      title='Disable LOS?'
      content={
        <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(4) }}>
          <Text variant='body1'>
            เมื่อทำการ Disable แล้ว{' '}
            <Text variant='body1Semibold' component='span'>
              จะไม่สามารถย้อนกลับได้
              <br />
              และไม่สามารถนำข้อมูล LOS ไปใช้ได้อีก
            </Text>
          </Text>
          <Text variant='body1'>กรุณาตรวจสอบความถูกต้องให้เรียบร้อยก่อนดำเนินการ</Text>
        </Flex>
      }
      acceptText='Disable LOS'
      variant='warning'
      open={isOpen}
      loading={isDisablingLOS || isLoadingLOS}
      acceptButtonProps={{ disabled: !chapterLevelItemId }}
      onAccept={handleDisable}
      onDiscard={onClose}
      onClose={onClose}
      disableEscapeKeyDown={isDisablingLOS}
    />
  );
}
